import { lazy, Suspense } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'

import { isDemoPresentation } from '@/lib/constants'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { integrationQueries } from '@/features/integrations/queries'

import { isApiError } from '@/shared/api/utils/error'
import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const IntegrationDetails = lazy(
	() => import('@/features/integrations/components/details'),
)

export const Route = createFileRoute('/_dashboard/integrations/cloud/$id')({
	beforeLoad: () => roleAuthGuard(ROLES.SystemManager),

	loader: async ({ params, context: { queryClient } }) => {
		const { id } = params
		const tenant = getCurrentTenant() as string

		if (!tenant) return

		try {
			await queryClient.ensureQueryData({
				...integrationQueries.detail(tenant, id),
				revalidateIfStale: true,
			})
		} catch (error) {
			// Demo containment (ticket 04): the demo world connects exactly one
			// connector, so any other id — pasted, stale, or guessed — 404s. Send it
			// back to the list rather than showing a prospect an error screen. Real
			// builds keep the error component: there a 404 is genuine signal.
			//
			// Narrowed to 404 on purpose. A blanket catch would also swallow a broken
			// handler or a schema drift into a silent bounce, which is the one failure
			// mode nobody can debug at a prospect's desk (AGENTS: never fail silently).
			const isNotFound = isApiError(error) && error.status === 404

			if (isDemoPresentation && isNotFound) {
				throw redirect({
					to: '/integrations/cloud',
					search: true,
					replace: true,
				})
			}

			throw error
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Integration Details | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: IntegrationsLoadingPage,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { id } = Route.useParams()

	return (
		<ErrorBoundary feature="integrations" section="details" entityId={id}>
			<Suspense fallback={<Skeleton className="h-[200px] w-full max-w-3xl" />}>
				<IntegrationDetails id={id} />
			</Suspense>
		</ErrorBoundary>
	)
}

function IntegrationsLoadingPage() {
	return <Skeleton className="h-[200px] w-full max-w-3xl" />
}
